import { formatTicker } from '@api/mappers/formatTicker';
import { RangeHorizon } from '@chart';
import { Icon } from '@design/components/icon/Icon';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { isNonZeroReturn } from '@helpers/utils/asset-price/asset-price';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { LivePriceView } from '@terminal/components/live-price-view/LivePriceView';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import {
  getLivePriceDataKey,
  mapEntityToPriceEntity,
  useLivePrice,
} from '@terminal/hooks/use-live-price';
import { ChartPreviewDropdown } from '@terminal/modules/chart-preview-dropdown/ChartPreviewDropdown';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './AssetChip.module.scss';

export interface AssetChipProps {
  entityTag: string;
  onEntityClick(entityTag: string): void;
}

export const AssetChip = ({ entityTag, onEntityClick }: AssetChipProps) => {
  const { t } = useTranslation('news');
  const { data } = useEntity(entityTag);
  const entity = data?.data;

  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);
  const livePriceState = useLivePrice(priceEntities);
  const livePriceDataKey = entity ? (getLivePriceDataKey(entity) ?? '') : '';
  const livePriceInfo = livePriceState[livePriceDataKey];
  const showPrice = isNonZeroReturn(
    livePriceInfo?.priceChange.proportionChange
  );

  if (!entity) {
    return null;
  }

  return (
    <div className={styles.assetChipRoot} data-testid="asset-chip">
      <ChartPreviewDropdown
        horizon={RangeHorizon.OneMonth}
        enableHover={false}
        entity={entity}
        side="bottom"
        align="end"
        snakeMeta={snakeMeta}
        source={POSTHOG_TRACKING_SOURCES.MARKET_SUMMARY_DRAWER}
      >
        <button
          className={styles.entityButton}
          data-testid="entity-button"
          onClick={() => onEntityClick(entityTag)}
        >
          <div className={styles.entity}>
            <AssetLogoContainer
              className={styles.assetLogoContainer}
              entity={entity}
              size={16}
            />
            <span className={styles.ticker} data-testid="asset-ticker">
              {formatTicker(entity)}
            </span>
          </div>

          {showPrice && (
            <LivePriceView
              livePriceInfo={livePriceInfo}
              lastTimestamp={snakeMeta?.last_timestamp}
              showMarketIndicator={false}
              showPriceChange={false}
            />
          )}
        </button>
      </ChartPreviewDropdown>

      <WebOnlyFeat>
        <WatchlistTrigger
          tag={entityTag}
          dropdownProps={{
            strategy: 'fixed',
            placement: 'top-start',
            inPortal: false,
          }}
          renderTrigger={isInWatchlist => (
            <span>
              <Tooltip
                label={t('news:tooltips.watchlistButton')}
                inPortal
                offsetX={-3}
              >
                <button
                  className={clsx(styles.watchlistButton, {
                    [styles.isActive]: isInWatchlist,
                  })}
                  data-testid="watchlist-button"
                >
                  <span className={styles.iconWrapper}>
                    <Icon
                      iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                      size={12}
                    />
                  </span>
                </button>
              </Tooltip>
            </span>
          )}
        />
      </WebOnlyFeat>
    </div>
  );
};
